import axios, { AxiosError, AxiosInstance } from 'axios';
import { clearTokens, getAccessToken, saveTokens } from '../utils/auth';

type RefreshResponse = { accessToken: string };

const api: AxiosInstance = axios.create({
  baseURL: (process.env.REACT_APP_BACKEND_URL as string | undefined) || '/api',
  withCredentials: true,
  timeout: 15000,
});

api.defaults.headers.get = api.defaults.headers.get || {};
api.defaults.headers.get['Cache-Control'] = 'no-cache';
api.defaults.headers.get['Pragma'] = 'no-cache';
api.defaults.headers.get['If-Modified-Since'] = '0';

function toPublicError(err: AxiosError): Error {
  const status = err.response?.status;

  const serverMsgRaw =
    (err.response?.data as any)?.message ?? (err.response?.data as any)?.error ?? undefined;
  const serverMsg =
    typeof serverMsgRaw === 'string' && serverMsgRaw.length <= 140 ? serverMsgRaw : undefined;

  const map: Record<number, string> = {
    400: serverMsg || 'Некорректные данные',
    401: serverMsg || 'Не авторизован',
    403: 'Нет доступа',
    404: 'Сервис недоступен',
    429: 'Слишком много запросов',
    500: 'Ошибка сервера',
  };

  if (!err.response) return new Error('Сервер недоступен');
  return new Error(map[status ?? 0] || 'Что-то пошло не так');
}

api.interceptors.request.use(config => {
  const access = getAccessToken();
  if (access) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${access}`;
  }

  const csrf = document.cookie
    .split('; ')
    .find(c => c.startsWith('csrf='))
    ?.split('=')[1];
  if (csrf) {
    config.headers = config.headers ?? {};
    (config.headers as any)['X-CSRF-Token'] = csrf;
  }

  return config;
});

let refreshing = false;
let waiters: Array<() => void> = [];

api.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const original: any = error.config || {};
    const status = error.response?.status;
    const url = (original.url || '') as string;

    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');
    const canRetry = status === 401 && !original._retry && !isAuthEndpoint;

    if (!canRetry) {
      throw toPublicError(error);
    }

    original._retry = true;

    if (refreshing) {
      await new Promise<void>(res => waiters.push(res));
      const access = getAccessToken();
      if (access) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${access}`;
        try {
          return await api(original);
        } catch (e) {
          throw toPublicError(e as AxiosError);
        }
      }
      clearTokens();
      throw new Error('Не авторизован');
    }

    try {
      refreshing = true;
      const { data } = await api.post<RefreshResponse>('/auth/refresh');
      saveTokens({ accessToken: data.accessToken });

      waiters.forEach(res => res());
      waiters = [];

      original.headers = original.headers ?? {};
      original.headers.Authorization = `Bearer ${data.accessToken}`;
      return await api(original);
    } catch (e) {
      waiters.forEach(res => res());
      waiters = [];
      clearTokens();
      throw toPublicError(e as AxiosError);
    } finally {
      refreshing = false;
    }
  },
);

export default api;
